import { useState, useCallback, useEffect } from 'react';
import mapboxgl from 'mapbox-gl';
import debounce from '../helpers/debounce';
import useOnFetch from './useOnFetch';
import { reverseGeocode } from './useGeoCodeLocation';

/**
 * @param {React.RefObject} mapRef - reference to the Mapbox map instance
 */
function useMapboxLocation(mapRef) {
    const fetch = useOnFetch();
    const [results, setResults] = useState([]);
    const [latLong, setLatLong] = useState(null);
    const [loading, setIsLoading] = useState(false);
    const [placeName, setPlaceName] = useState(null);

    const updateLatLong = useCallback(
        (lat, lng) => {
            setLatLong([lat, lng]);

            if (!mapRef.current) return;

            mapRef.current.flyTo({ center: [lng, lat], zoom: 15 });

            mapRef.current
                .getCanvas()
                .parentElement.querySelectorAll('.mapboxgl-marker')
                .forEach((marker) => marker.remove());

            const marker = new mapboxgl.Marker({ color: '#0d9488', draggable: true }).setLngLat([lng, lat]).addTo(mapRef.current);

            marker.on('dragend', () => {
                const { lat, lng } = marker.getLngLat();
                setLatLong([lat, lng]);
            });
        },
        [mapRef]
    );

    const handleInputChange = debounce(async (search) => {
        const isSearchFilterActive = search.length >= 3;

        if (!isSearchFilterActive) {
            setResults([]);
            return;
        }

        try {
            setIsLoading(true);
            const { data } = await fetch({
                methodL: 'get',
                url: '/locations/autocomplete',
                params: { query: search },
            });

            setIsLoading(false);
            setResults(
                data?.suggestions?.map((item) => ({
                    label: item?.text,
                    value: [item?.lat, item?.lng],
                })) || []
            );
        } catch {
            setResults([]);
        }
    }, 500);

    const handleSelect = useCallback(
        (place) => {
            const [lat, lng] = place || [];
            updateLatLong(lat, lng);
            setResults([]);
        },
        [updateLatLong]
    );

    const goToMyLocation = useCallback(() => {
        if (!navigator.geolocation) {
            console.warn('Geolocation is not supported by your browser');
            return;
        }

        navigator.geolocation.getCurrentPosition(
            async ({ coords: { latitude: lat, longitude: lng } }) => {
                updateLatLong(lat, lng);
            },
            (err) => {
                console.warn(`Unable to retrieve your location: ${err.message}`);
            },
            { enableHighAccuracy: true }
        );
    }, [updateLatLong]);

    const fetchPlaceName = async (isMounted) => {
        if (latLong?.length > 1) {
            try {
                const [lat, lng] = latLong;
                const data = await reverseGeocode(lat, lng, fetch);
                if (isMounted) setPlaceName(data);
            } catch (error) {
                if (isMounted) setPlaceName(null);
                console.error('Error fetching place name:', error);
            }
        } else {
            setPlaceName(null);
        }
    };

    useEffect(() => {
        let isMounted = true;
        fetchPlaceName(isMounted);

        return () => {
            isMounted = false;
        };
    }, [latLong, fetch]);

    return {
        results,
        latLong,
        updateLatLong,
        handleInputChange,
        handleSelect,
        goToMyLocation,
        loading,
        placeName,
    };
}

export default useMapboxLocation;
